import React, { ChangeEvent, Ref, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@mdi/react';
import { mdiCheck, mdiPlus, mdiTrashCanOutline } from '@mdi/js';
import uuid from 'uuid';
import clsx from 'clsx';
import { fnStartMarker, stringToObject, objectToString } from 'relevant-shared/misc/funcJson';
import { Button } from '../Button/Button';
import { SegmentedSelect } from '../SegmentedSelect';
import TextEditor from './textEditor';
import { IconButton } from '../Button/IconButton';
import { Input } from '../Input';
import { Select } from '../Select/Select';

const modes = [
	{ id: 'structured', text: 'Visual' },
	{ id: 'raw', text: 'JSON' },
] as const;

interface ParametersEditorProps {
	value: string;
	name: string;
	onChange: ({ target }: { target: { name: string, value: string } }) => void;
	error: boolean;
	title?: string;
	fitWithOldDesign?: boolean;
	readOnly?: boolean;
	satisfy?: unknown;
}

const allExcept = <T, >(arr: T[], idx: number) => [...arr.slice(0, idx), ...arr.slice(idx + 1)];

const getType = (value: unknown) => {
	if (value === null) return 'null';
	if (Array.isArray(value)) return 'array';
	if (typeof value === 'string' && value.startsWith(fnStartMarker)) return 'function';
	return typeof value;
};
type EditableInStructured = 'string' | 'number' | 'boolean' | 'null';
const isPrimitive = (type: string): type is EditableInStructured => ['string', 'number', 'boolean', 'null'].includes(type);

export function ParametersEditor({
	value,
	name,
	onChange,
	error,
	title,
	fitWithOldDesign = false,
	readOnly = false,
	satisfy,
}: ParametersEditorProps) {
	const [selectedMode, setSelectedMode] = useState<'structured' | 'raw'>('structured');
	const rowIds = useRef<string[]>([]);
	const [newRow, setNewRow] = useState<[string, unknown, string] | null>(null);
	const newInputRef = useRef<HTMLInputElement>();

	const valueArr = useMemo<[string, unknown][]>(() => {
		try {
			const parsed = stringToObject(value);
			const entries = Object.entries(parsed);
			while (rowIds.current.length < entries.length) {
				rowIds.current.push(uuid.v4());
			}
			rowIds.current.length = entries.length;
			return entries;
		} catch (e) {
			return [];
		}
	}, [value]);

	const allProperties = [
		...valueArr.map((kv) => kv[0]),
		newRow?.[0],
	];

	useEffect(() => {
		try {
			const obj: Record<string, unknown> = stringToObject(value);
			if (Object.values(obj).some((val) => !isPrimitive(getType(val)))) {
				setSelectedMode('raw');
			}
		} catch (e) {
			setSelectedMode('raw');
		}
	}, []);

	const handleEntryChange = (newEntry: [string, unknown], idx: number) => {
		const newObj = Object.fromEntries(valueArr.map((kv, i) => (i === idx ? newEntry : kv)));
		onChange({ target: { name, value: objectToString(newObj, true) } });
	};

	const handleAddEmptyRow = () => {
		setNewRow([null, null, uuid.v4()]);
		setTimeout(() => newInputRef.current?.focus(), 0);
	};

	const handleDeleteRow = (idx: number) => {
		const newObj = Object.fromEntries(valueArr.filter((_, i) => i !== idx));
		onChange({ target: { name, value: objectToString(newObj, true) } });
	};

	const handleConfirmRow = () => {
		const newObj = Object.fromEntries([...valueArr, newRow]);
		rowIds.current.push(newRow[2]);
		setNewRow(null);
		onChange({ target: { name, value: objectToString(newObj, true) } });
	};

	return (
		<div className={clsx(
			'flex flex-col p-6 gap-6 rounded-m',
			!fitWithOldDesign && 'border border-borders-primary',
		)}>
			<div className="flex justify-between">
				<span className={clsx(
					fitWithOldDesign ? 'relative left-[-24px] text-xl text-[rgb(49,50,51)] font-light' : 'text-2xl text-text-tertiary',
				)}>{title ?? 'Parameters editor:'}</span>
				<div className="flex gap-2xs-12">
					{!readOnly && selectedMode === 'structured' && (
						<Button
							variant="ghost"
							onClick={handleAddEmptyRow}
							disabled={!!newRow}
						>
							<Icon path={mdiPlus} size="20px" />
							New parameter
						</Button>
					)}
					<SegmentedSelect
						items={modes}
						selected={[selectedMode]}
						onChange={([newMode]) => setSelectedMode(newMode)}
						disabled={error} // don't let switching if there is an error, which should only happen when mode === 'raw'
						noEmptySelected
					/>
				</div>
			</div>
			{selectedMode === 'structured' ? (
				<div className="flex flex-col gap-3xs-8">
					<div className="grid grid-cols-[250px_135px_230px_auto] gap-xs-16 text-text-tertiary text-xs font-semibold">
						<span>Property name</span>
						<span>Type</span>
						<span>Value</span>
					</div>
					<div className="grid grid-cols-[250px_135px_230px_auto] gap-xs-16">
						{valueArr.map(([property, propertyValue], idx) => (
							<ParameterRow
								key={rowIds.current[idx]}
								property={property}
								onChangeEntry={(newEntry) => handleEntryChange(newEntry, idx)}
								otherProperties={allExcept(allProperties, idx)}
								value={propertyValue}
								onDelete={() => handleDeleteRow(idx)}
								readOnly={readOnly}
							/>
						))}
						{!!newRow && (
							<ParameterRow
								key={newRow[2]}
								property={newRow[0]}
								onChangeEntry={(newEntry) => setNewRow([...newEntry, newRow[ 2 ]])}
								otherProperties={allExcept(allProperties, valueArr.length)}
								value={newRow[1]}
								onDelete={() => setNewRow(null)}
								onConfirm={() => handleConfirmRow()}
								inputRef={newInputRef}
							/>
						)}
					</div>
				</div>
			) : (
				<TextEditor
					width="100%"
					height={readOnly ? `${value.split('\n').length * 14}px` : '170px'}
					value={value}
					onChange={onChange}
					name={name}
					error={error}
					satisfy={satisfy}
					readOnly={readOnly}
					showGutter={!readOnly}
				/>
			)}
		</div>
	);
}

const typeOptions = [
	{ value: 'string', label: 'String' },
	{ value: 'number', label: 'Number' },
	{ value: 'boolean', label: 'Boolean' },
	{ value: 'null', label: 'Null' },
];

const boolOptions = [
	{ value: true, label: 'True' },
	{ value: false, label: 'False' },
];

interface ParameterRow {
	property: string;
	onChangeEntry: (entry: [string, unknown]) => void;
	otherProperties: string[];
	value: unknown;
	onDelete: () => void;
	onConfirm?: () => void;
	inputRef?: Ref<HTMLInputElement>;
	readOnly?: boolean;
}

function ParameterRow({
	property,
	onChangeEntry,
	otherProperties,
	value,
	onDelete,
	onConfirm,
	inputRef,
	readOnly = false,
}: ParameterRow) {
	const [localProperty, setLocalProperty] = useState(property);

	useEffect(() => {
		setLocalProperty(property);
	}, [property]);

	const type = getType(value);
	const editable = isPrimitive(type);
	const empty = property === null;
	const isDuplicate = !!otherProperties.filter((p) => p === localProperty).length;

	const handleChangeKey = (e: ChangeEvent<HTMLInputElement>) => {
		const newKey = e.target.value.trim();
		setLocalProperty(newKey);
		if (!otherProperties.filter((p) => p === newKey).length) {
			onChangeEntry([newKey, value]);
		}
	};

	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		if (newValue === '') return onChangeEntry([property, null]);
		switch (type) {
			case 'null':
				return onChangeEntry([property, null]);
			case 'string':
				return onChangeEntry([property, newValue]);
			case 'number':
				return onChangeEntry([property, parseFloat(newValue)]);
			case 'boolean':
				return onChangeEntry([property, newValue === 'true']);
			default:
				return null;
		}
	};

	const handleChangeType = (newType: EditableInStructured) => {
		switch (newType) {
			case 'null':
				return onChangeEntry([property, null]);
			case 'string':
				return type === 'number' || type === 'boolean' ? onChangeEntry([property, `${value}`]) : onChangeEntry([property, '']);
			case 'number':
				if (type === 'string') {
					try {
						const parsed = Number(value as string);
						return onChangeEntry([property, Number.isNaN(parsed) ? 0 : parsed]);
					} catch (e) {
						return onChangeEntry([property, 0]);
					}
				}
				return onChangeEntry([property, 0]);
			case 'boolean':
				return onChangeEntry([property, type === 'string' && value === 'true']);
			default:
				return null;
		}
	};

	return (
		<>
			<Input
				ref={inputRef}
				type="text"
				value={localProperty}
				onChange={handleChangeKey}
				onBlur={isDuplicate ? () => setLocalProperty(property) : undefined}
				invalid={isDuplicate ? 'Duplicate key, change value of existing instance instead' : undefined}
				disabled={readOnly}
			/>
			<Select
				value={editable ? type : 'notEditable'}
				onChange={handleChangeType}
				options={editable ? typeOptions : [{ value: 'notEditable', label: readOnly ? 'Not visible' : 'Not editable' }]}
				disabled={readOnly || empty || !editable}
				size="md2"
				fullWidth

			/>
			{editable && type === 'boolean' && (
				<Select
					value={value}
					onChange={(newVal) => onChangeEntry([property, newVal])}
					options={boolOptions}
					size="md2"
					disabled={readOnly}
					fullWidth
				/>
			)}
			{editable && type === 'null' && (
				<Input type="text" value="Null" disabled />
			)}
			{editable && type !== 'boolean' && type !== 'null' && (
				<Input type={type === 'string' ? 'text' : 'number'} value={value as string | number} onChange={handleChangeValue} disabled={readOnly} />
			)}
			{!editable && (
				<span className="flex items-center text-text-primary">{readOnly ? 'See' : 'Edit'} in code view!</span>
			)}
			{!readOnly && !onConfirm && <IconButton icon={<Icon path={mdiTrashCanOutline} size="20px" />} onClick={onDelete} disabled={readOnly} />}
			{!!onConfirm && !empty && <IconButton icon={<Icon path={mdiCheck} size="20px" />} onClick={onConfirm} disabled={readOnly || isDuplicate} />}
			{(readOnly || (!!onConfirm && empty)) && <div />} {/* preserve grid layout */}
		</>
	);
}
